import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import { useAuth } from "@/features/auth/auth-provider";
import {
  COMPANY_SIZE_OPTIONS,
  createBetaAccessRequestSchema,
  submitBetaAccessRequest,
} from "@/features/auth/request-access-api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoBanner } from "@/components/design/info-banner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RequestAccessPage() {
  const navigate = useNavigate();
  const { email: sessionEmail, authenticated, logout } = useAuth();

  async function handleSignIn(): Promise<void> {
    // A signed-in Privy session (how most users reach this page) makes /login
    // bounce straight back here, so sign out first to reach the login screen.
    if (authenticated) {
      await logout();
    }
    navigate("/login");
  }
  const [name, setName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companySize, setCompanySize] = useState<string>(COMPANY_SIZE_OPTIONS[0]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Get access to Werkio Beta — ${APP_NAME}`;
  }, []);

  useEffect(() => {
    if (sessionEmail && !formEmail) {
      setFormEmail(sessionEmail);
    }
  }, [sessionEmail, formEmail]);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const parsed = createBetaAccessRequestSchema.safeParse({
      name,
      email: formEmail,
      companyName,
      companySize,
      message: message || undefined,
    });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please check the form and try again.");
      setSubmitting(false);
      return;
    }

    try {
      await submitBetaAccessRequest(parsed.data);
      navigate("/request-access/success", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not submit your request.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <HardHat className="h-6 w-6" />
          </div>
          <CardTitle>Get access to Werkio Beta</CardTitle>
          <CardDescription>
            Werkio is currently in private beta. Enter your details and we&apos;ll review your
            access request.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName">Company name</Label>
              <Input
                id="companyName"
                autoComplete="organization"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companySize">Company size</Label>
              <select
                id="companySize"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                required
              >
                {COMPANY_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message (optional)</Label>
              <textarea
                id="message"
                className="flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us about your crew, sites, or payroll workflow."
              />
            </div>
            {error ? (
              <InfoBanner variant="error" title="Could not submit">
                {error}
              </InfoBanner>
            ) : null}
            <Button className="w-full" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit request"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            <Link to="/demo" className="font-medium text-orange-construction hover:underline">
              Try the demo
            </Link>
            {" · "}
            <button
              type="button"
              onClick={() => void handleSignIn()}
              className="font-medium hover:underline"
            >
              {authenticated ? "Sign in with a different email" : "Sign in"}
            </button>
            {" · "}
            <Link to="/" className="font-medium hover:underline">
              Home
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
